/**
 * API client — calls the API via Firebase Hosting rewrite (/api/** → Function).
 * In dev mode (localhost), uses the direct Function URL.
 */

import { getAuth, onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';

const DIRECT_FUNCTION_URL = 'https://apiagenticstoryengine-pyt4kllmdq-uc.a.run.app';
const IS_DEV = window.location.hostname === 'localhost';
const FUNCTION_URL = IS_DEV ? DIRECT_FUNCTION_URL : '';

let authReady: Promise<void> | null = null;

function waitForAuth(): Promise<void> {
  if (authReady) return authReady;

  authReady = new Promise((resolve) => {
    const auth = getAuth();
    if (auth.currentUser) {
      resolve();
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      resolve();
    });
    setTimeout(resolve, 10000);
  });

  return authReady;
}

async function getAuthToken(): Promise<string> {
  await waitForAuth();
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return '';
  return user.getIdToken();
}

export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAuthToken();
  const url = `${FUNCTION_URL}${path}`;

  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
}

export function apiUrl(path: string): string {
  return `${FUNCTION_URL}${path}`;
}

/**
 * Exchange the verified Firebase ID token for a __session cookie so
 * <img>/<video>/<a download> asset requests — which can't send an
 * Authorization header — are authenticated at the proxy. Called once after
 * auth resolves and again on every ID-token refresh (see bindSessionRefresh).
 */
export async function establishSession(): Promise<void> {
  const token = await getAuthToken();
  if (!token) return;
  try {
    await fetch(`${FUNCTION_URL}/api/session-login`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    /* asset auth will retry on the next token refresh */
  }
}

let sessionRefreshBound = false;
/** Keep the __session cookie fresh as Firebase rotates the ID token (~hourly). */
export function bindSessionRefresh(): void {
  if (sessionRefreshBound) return;
  sessionRefreshBound = true;
  onIdTokenChanged(getAuth(), (user) => {
    if (user) void establishSession();
  });
}

export interface Candidate {
  index: number;
  path: string; // e.g. locations/candidates/NAV_ROOM_c0.png
  score: number;
  gemini: number;
  claude: number;
  approved: boolean;
}

export interface CandidateEntry {
  name: string;
  safe_name?: string;
  location_id?: string;
  canonical: string;
  chosen_index: number;
  chosen_by: 'ai' | 'human';
  candidates: Candidate[];
}

/** Fetch the retained candidate slate manifest for a production's locations, props,
 *  or storyboard keyframes (keyed by shot_id). */
export async function fetchCandidateManifest(
  productionId: string,
  assetType: 'location' | 'prop' | 'keyframe',
): Promise<Record<string, CandidateEntry>> {
  const file =
    assetType === 'location'
      ? 'location_candidates.json'
      : assetType === 'keyframe'
        ? 'keyframe_candidates.json'
        : 'prop_candidates.json';
  try {
    const resp = await apiFetch(`/api/assets/${productionId}/${file}`);
    if (!resp.ok) return {};
    return (await resp.json()) as Record<string, CandidateEntry>;
  } catch {
    return {};
  }
}

/** POST-HOC swap: pick a different location/prop/keyframe candidate after a phase
 *  completed. A keyframe swap promotes the take and re-shoots the shot's clip. */
export async function selectCandidate(
  productionId: string,
  body: { asset_type: 'location' | 'prop' | 'keyframe'; asset_id: string; candidate_index: number },
): Promise<Response> {
  return apiFetch(`/api/select-candidate/${productionId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Rename a project (owner only). The engine field is `title`. */
export async function renameProject(projectId: string, title: string): Promise<Response> {
  return apiFetch(`/api/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}

/** Delete a project and all its productions/assets (owner only, irreversible). */
export async function deleteProject(projectId: string): Promise<Response> {
  return apiFetch(`/api/projects/${projectId}`, { method: 'DELETE' });
}

export async function sseUrl(path: string): Promise<string> {
  const token = await getAuthToken();
  const base = `${DIRECT_FUNCTION_URL}${path}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}
